import dayjs from 'dayjs';

import type { GarmentJob } from '../api/garmentJobs';

import { actionsFor, isOut } from './queue';

/**
 * Finishing, sending round again, reporting damage on, or moving the date of one job — the
 * website's close panel and the phone's job sheet, one set of rules.
 *
 * <p>
 * <strong>The consequence of each is stated, not left to the verb</strong>: "send back" and
 * "finish" look alike on a busy counter and do opposite things to the availability calendar — one
 * keeps the garment off the rail, the other puts it back.
 * </p>
 */

export type CloseAction = 'complete' | 'reClean' | 'damaged' | 'extend';

export const closeCopy: Record<
  CloseAction,
  { label: string; title: string; commit: string; hint: string }
> = {
  complete: {
    label: 'Finish',
    title: 'Finish this job',
    commit: 'Finish',
    hint: 'The garment goes back on the rail — its block is released and the calendar can offer it again.',
  },
  reClean: {
    label: 'Again',
    title: 'Send it round again',
    commit: 'Send back',
    hint: 'It stays off the rail. A garment going round again is not back, so the calendar must not offer it.',
  },
  damaged: {
    label: 'Damaged',
    title: 'Report damage',
    commit: 'Report',
    hint: 'Closes the job and records the incident against the vendor. What happens to the garment next is your decision — this does not retire it.',
  },
  extend: {
    label: 'New date',
    title: 'Push the date back',
    commit: 'Move the date',
    hint: 'Stretches the garment’s block with it, so the calendar does not offer something still at the vendor.',
  },
};

/**
 * The words for one action on this job. Finishing a garment that is still marked out is the counter
 * saying it came back and is fine — the server stamps it received as it finishes it — so it is
 * named for that rather than for the bookkeeping.
 */
export function closeWords(job: GarmentJob, action: CloseAction): (typeof closeCopy)[CloseAction] {
  if (action === 'complete' && isOut(job)) {
    return {
      label: 'Back — good',
      title: 'Back, and good',
      commit: 'Back — good',
      hint: closeCopy.complete.hint,
    };
  }

  return closeCopy[action];
}

/** What can be done to this job from a queue, in the order a counter reaches for them. */
export function closeActions(job: GarmentJob): CloseAction[] {
  const can = actionsFor(job);

  return (['complete', 'reClean', 'extend', 'damaged'] as const).filter((action) => can[action]);
}

/** What the person has typed — the cost as text, because a field holds text until it is sent. */
export interface CloseDraft {
  cost: string;
  notes: string;
  dueOn: string | null;
}

export function closeDraft(job: GarmentJob): CloseDraft {
  return { cost: job.cost === null ? '' : String(job.cost), notes: '', dueOn: job.dueOn };
}

/** The furthest a promised date may be, in days — the server's `MaxDaysOut`. */
export const maxDaysOut = 365;

/**
 * Why a promised date would be refused, or null — the server's `EnsureDueDateIsSane`: not before
 * today, and not more than a year out, which is a slipped year rather than a real promise.
 */
export function dueDateProblem(dueOn: string | null, today: string): string | null {
  if (dueOn === null || dueOn === '') {
    return null;
  }

  if (dueOn < today) {
    return 'The date has already gone. Choose today or later.';
  }

  if (dueOn > dayjs(today).add(maxDaysOut, 'day').format('YYYY-MM-DD')) {
    return 'That is more than a year away. Check the year.';
  }

  return null;
}

/** Why this close would be refused, in the order a person fixes them. Empty means it would go. */
export function closeProblems(action: CloseAction, draft: CloseDraft, today: string): string[] {
  const problems: string[] = [];

  if ((action === 'reClean' || action === 'damaged') && draft.notes.trim() === '') {
    problems.push(
      'Say what was wrong with it — a disagreement with the vendor is settled from this.',
    );
  }

  if (action === 'extend') {
    const date = dueDateProblem(draft.dueOn, today);

    if (draft.dueOn === null || draft.dueOn === '') {
      problems.push('Choose the new date.');
    } else if (date) {
      problems.push(date);
    }
  }

  if (action === 'complete' && draft.cost.trim() !== '') {
    const cost = Number(draft.cost);

    if (!Number.isFinite(cost) || cost < 0) {
      problems.push('The cost has to be a figure, nothing or more.');
    }
  }

  return problems;
}

/** What the server is sent. Only the fields this action reads, so nothing is changed by accident. */
export interface CloseRequest {
  cost?: number | null;
  notes: string | null;
  dueOn?: string;
}

export function closeRequest(action: CloseAction, draft: CloseDraft): CloseRequest {
  const notes = draft.notes.trim() === '' ? null : draft.notes.trim();

  return {
    ...(action === 'complete'
      ? { cost: draft.cost.trim() === '' ? null : Number(draft.cost) }
      : {}),
    ...(action === 'extend' && draft.dueOn ? { dueOn: draft.dueOn } : {}),
    notes,
  };
}

/** The endpoint each action posts to. */
export const closeRoute: Record<CloseAction, string> = {
  complete: 'complete',
  reClean: 're-clean',
  damaged: 'damaged',
  extend: 'extend',
};
